import { Package } from '@features/DynamicPackagesConfigurationWizard/types';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { ConfigurationGroupType, TimeSlotsType } from 'shared-types/SpaPackage';
import { buildDatesToCheck } from './buildDatesToCheck';
import { isSpaPackageUnavailable } from './isSpaPackageUnavailable';

export const buildConfigGroupAvailabilityMap = (
  configGroupResults: Array<{ date: string; configGroups: ConfigurationGroupType[] | undefined }>,
  numberOfGuests: number
): Map<number, { dates: string[]; availableDates: string[] }> => {
  const configGroupAvailabilityMap = new Map<
    number,
    { dates: string[]; availableDates: string[] }
  >();

  configGroupResults.forEach(({ date, configGroups }) => {
    if (!configGroups) return;

    configGroups.forEach((group) => {
      if (!configGroupAvailabilityMap.has(group.id)) {
        configGroupAvailabilityMap.set(group.id, { dates: [], availableDates: [] });
      }

      const groupData = configGroupAvailabilityMap.get(group.id)!;
      groupData.dates.push(date);

      const isAvailable = !isSpaPackageUnavailable([group], numberOfGuests);
      if (isAvailable) {
        groupData.availableDates.push(date);
      }
    });
  });

  return configGroupAvailabilityMap;
};

export const validateSpaPackageConfigGroupAvailability = (
  packageData: Package,
  configGroupAvailabilityMap: Map<number, { dates: string[]; availableDates: string[] }>,
  journeyParams: ParsedJourneyParams,
  isPackageRepeating: boolean
): boolean => {
  if (!packageData.experiences || packageData.experiences.length === 0) {
    return true;
  }

  if (isPackageRepeating) {
    if (!journeyParams.toDate) return false;

    //stayDuration includes checkout day
    const stayDuration = journeyParams.toDate.diff(journeyParams.fromDate, 'days') + 1;
    const minimumRequiredDays = Math.max(1, stayDuration - 1);

    const allExperienceAvailableDates: string[][] = [];

    for (const experience of packageData.experiences) {
      const configGroupId = Number(experience.meta.entityId);
      const groupAvailability = configGroupAvailabilityMap.get(configGroupId);

      if (!groupAvailability) return false;

      const experienceDates = buildDatesToCheck(
        journeyParams,
        experience.dayNumber,
        experience.dayNumberRange,
        isPackageRepeating
      );

      const availableDatesForExperience = experienceDates.filter((date) =>
        groupAvailability.availableDates.includes(date)
      );

      allExperienceAvailableDates.push(availableDatesForExperience);
    }

    if (allExperienceAvailableDates.length === 0) {
      return false;
    }

    const intersection = allExperienceAvailableDates.reduce((acc, dates) => {
      return acc.filter((date) => dates.includes(date));
    }, allExperienceAvailableDates[0]);

    return intersection.length >= minimumRequiredDays;
  }

  return packageData.experiences.every((experience) => {
    const configGroupId = Number(experience.meta.entityId);
    const groupAvailability = configGroupAvailabilityMap.get(configGroupId);

    if (!groupAvailability) return false;

    const experienceDates = buildDatesToCheck(
      journeyParams,
      experience.dayNumber,
      experience.dayNumberRange,
      isPackageRepeating
    );

    return experienceDates.some((date) => groupAvailability.availableDates.includes(date));
  });
};

export const validateSpaPackageTimeBandAvailability = (
  isPackageRepeating: boolean,
  journeyParams: ParsedJourneyParams,
  timeBandDates: TimeSlotsType
): boolean => {
  if (isPackageRepeating) {
    if (!journeyParams.toDate) return false;
    //stayDuration includes checkout day
    const stayDuration = journeyParams.toDate.diff(journeyParams.fromDate, 'days') + 1;
    const minimumRequiredDays = Math.max(1, stayDuration - 1);

    const datesWithAvailability = Object.keys(timeBandDates).reduce<string[]>((dates, date) => {
      const timeslotsOnDay = Object.values(timeBandDates[date]).filter(
        (slot) => slot !== undefined
      );
      if (timeslotsOnDay.length === 0) return dates;
      return dates.concat(date);
    }, []);

    return datesWithAvailability.length >= minimumRequiredDays;
  } else {
    const experienceDates = buildDatesToCheck(journeyParams, 0, undefined, isPackageRepeating)[0];
    return experienceDates in timeBandDates && timeBandDates[experienceDates].length > 0;
  }
};
